"use client"
import { useState, useRef, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { postTeacherThumbnailCourse, generatePresignedUrl } from "../postFileAws"
import { postAws } from "../postAws"
import {
  Book,
  Loader,
  AlertCircle,
  Search,
  Plus,
  Star,
  Users,
  BarChartIcon as ChartBar,
  X,
  Youtube,
  Share2,
  Filter,
  ChevronDown,
  Grid3X3,
  List,
  Edit,
  Trash2,
} from "lucide-react"
import { YouTubeCourseModal } from "./youtube-course-modal"
import { CourseStructureModal } from "./course-structure-modal"

const FlashMessage = ({ message, onClose }) => (
  <div
    className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 flex items-center space-x-2 transform transition-all duration-500 ease-in-out animate-fade-in ${
      message.type === "success" ? "bg-green-500" : "bg-red-500"
    } text-white max-w-xs sm:max-w-sm`}
  >
    {message.type === "error" ? (
      <X className="h-5 w-5 flex-shrink-0" />
    ) : (
      <svg className="h-5 w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
          clipRule="evenodd"
        />
      </svg>
    )}
    <p>{message.text}</p>
    <button onClick={onClose} className="ml-2 text-white hover:text-gray-200">
      <X className="h-4 w-4" />
    </button>
  </div>
)

const ELearning = () => {
  const navigate = useNavigate()
  const [courses, setCourses] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isYouTubeModalOpen, setIsYouTubeModalOpen] = useState(false)
  const [isStructureModalOpen, setIsStructureModalOpen] = useState(false)
  const [newCourse, setNewCourse] = useState({
    name: "",
    description: "",
    thumbnail: null,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState(null)
  const [isDeletingCourse, setIsDeletingCourse] = useState(false)
  const [flashMessage, setFlashMessage] = useState(null)
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false)
  const [isProcessingYouTube, setIsProcessingYouTube] = useState(false)
  const [isCreatingCourse, setIsCreatingCourse] = useState(false)
  const [youtubeProcessingData, setYoutubeProcessingData] = useState({
    courseData: null,
    playlistData: null,
    topicsData: null,
  })
  const [activeFilter, setActiveFilter] = useState("all")
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const [viewMode, setViewMode] = useState("card") // New state for view toggle
  const fileInputRef = useRef(null)

  useEffect(() => {
    const fetchCourses = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const classRoomId = localStorage.getItem("classRoomId")
        // Make direct API call to /eLearning endpoint
        const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
          request: "get-all-courses",
          body: {
            classRoomId: classRoomId,
          },
        })
        if (response.statusCode === 200) {
          const responseData = JSON.parse(response.body)
          if (Array.isArray(responseData.courses)) {
            const formattedCourses = await Promise.all(
              responseData.courses.map(async (course) => {
                let thumbnailUrl = null
                if (course.thumbnailS3Uri) {
                  try {
                    thumbnailUrl = await generatePresignedUrl(course.thumbnailS3Uri)
                  } catch (error) {
                    console.error("Error generating presigned URL:", error)
                  }
                }
                return {
                  id: course.id || course._id,
                  name: course.title,
                  description: course.description,
                  thumbnail: thumbnailUrl,
                  rating: course.averageRating || 0,
                  enrolledStudents: course.numberOfStudentsShared || 0,
                  completionRate: course.averageCompletionPercentage || 0,
                  shareWithStudents: course.studentsSharedWith && course.studentsSharedWith.length > 0,
                }
              }),
            )
            setCourses(formattedCourses)
          } else {
            setCourses([])
          }
        } else {
          throw new Error("Invalid response from API")
        }
      } catch (err) {
        console.error("Error fetching courses:", err)
        setError(err.message)
      } finally {
        setIsLoading(false)
      }
    }
    fetchCourses()
  }, [])

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value)
  }

  const getFilteredCourses = () => {
    const filtered = courses.filter(
      (course) => course.name && course.name.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    switch (activeFilter) {
      case "shared":
        return filtered.filter((course) => course.shareWithStudents)
      case "not-shared":
        return filtered.filter((course) => !course.shareWithStudents)
      case "high-rating":
        return filtered.filter((course) => course.rating >= 4.0)
      default:
        return filtered
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setNewCourse({ ...newCourse, [name]: value })
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setNewCourse({ ...newCourse, thumbnail: file })
    }
  }

  const generateAIDescription = async () => {
    if (!newCourse.name.trim()) {
      setFlashMessage({ type: "error", text: "Please enter a course title first" })
      return
    }
    try {
      setIsGeneratingDescription(true)
      const payload = {
        request: "suggest-description",
        body: {
          topicTitle: newCourse.name,
        },
      }
      const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", payload)
      if (response.statusCode === 200) {
        const responseData = JSON.parse(response.body)
        const description = responseData.description
        let formattedDescription = `${description.intro}\n\n`
        if (description.whatYouWillLearn && description.whatYouWillLearn.length > 0) {
          formattedDescription += "What you'll learn:\n"
          description.whatYouWillLearn.forEach((item) => {
            formattedDescription += `• ${item}\n`
          })
          formattedDescription += "\n"
        }
        if (description.outcomes && description.outcomes.length > 0) {
          formattedDescription += "Course outcomes:\n"
          description.outcomes.forEach((item) => {
            formattedDescription += `• ${item}\n`
          })
        }
        setNewCourse({
          ...newCourse,
          description: formattedDescription,
        })
        setFlashMessage({ type: "success", text: "Description generated successfully!" })
      } else {
        throw new Error(JSON.parse(response.body).message || "Failed to generate description")
      }
    } catch (error) {
      console.error("Error generating description:", error)
      setFlashMessage({ type: "error", text: "Failed to generate description. Please try again." })
    } finally {
      setIsGeneratingDescription(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      const teacherId = localStorage.getItem("teacherId")
      const classRoomId = localStorage.getItem("classRoomId")
      if (!teacherId || !classRoomId) {
        throw new Error("Teacher ID or Classroom ID not found")
      }
      let thumbnailData = null
      if (newCourse.thumbnail) {
        thumbnailData = await postTeacherThumbnailCourse(
          newCourse.thumbnail,
          teacherId,
          classRoomId,
          newCourse.thumbnail.name,
          newCourse.name,
        )
      }
      const courseData = {
        title: newCourse.name,
        description: newCourse.description,
        teacherId: teacherId,
      }
      if (thumbnailData) {
        courseData.thumbnailS3Uri = thumbnailData.uri
      }
      const payload = {
        request: "add-course",
        body: {
          classRoomId,
          courseData,
        },
      }
      const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", payload)
      if (response.statusCode === 200) {
        const responseBody = JSON.parse(response.body)
        const newCourseData = responseBody.course
        let thumbnailUrl = null
        if (thumbnailData && thumbnailData.uri) {
          try {
            thumbnailUrl = await generatePresignedUrl(thumbnailData.uri)
          } catch (error) {
            console.error("Error generating presigned URL:", error)
          }
        }
        setCourses([
          ...courses,
          {
            id: newCourseData._id,
            name: newCourseData.title,
            description: newCourseData.description,
            thumbnail: thumbnailUrl,
            rating: 0,
            enrolledStudents: 0,
            completionRate: 0,
          },
        ])
        setIsModalOpen(false)
        setNewCourse({ name: "", description: "", thumbnail: null })
      } else {
        throw new Error(JSON.parse(response.body).message || "Failed to create course")
      }
    } catch (error) {
      console.error("Error creating course:", error)
      setError(`Error creating course: ${error.message}`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleYouTubeSubmit = async (formData) => {
    setIsProcessingYouTube(true)
    try {
      const teacherId = localStorage.getItem("teacherId")
      const classRoomId = localStorage.getItem("classRoomId")
      if (!teacherId || !classRoomId) {
        throw new Error("Teacher ID or Classroom ID not found")
      }
      let thumbnailData = null
      if (formData.thumbnail) {
        thumbnailData = await postTeacherThumbnailCourse(
          formData.thumbnail,
          teacherId,
          classRoomId,
          formData.thumbnail.name,
          formData.title,
        )
      }
      const coursePayload = {
        request: "add-course",
        body: {
          classRoomId,
          courseData: {
            title: formData.title,
            description: formData.description,
            teacherId: teacherId,
          },
        },
      }
      if (thumbnailData) {
        coursePayload.body.courseData.thumbnailS3Uri = thumbnailData.uri
      }
      const courseResponse = await postAws("https://api-dev.aurait.ai/eLearning", "POST", coursePayload)
      if (courseResponse.statusCode !== 200) {
        throw new Error("Failed to create course")
      }
      const courseData = JSON.parse(courseResponse.body).course
      setFlashMessage({ type: "success", text: "Course created! Now fetching YouTube playlist..." })
      const playlistResponse = await postAws("https://api-dev.aurait.ai/playlistFromYoutube", "POST", {
        playlist_url: formData.playlistUrl,
      })
      if (playlistResponse.statusCode !== 200) {
        throw new Error("Failed to fetch playlist data")
      }
      const playlistData = JSON.parse(playlistResponse.body)
      localStorage.setItem("youtubePlaylistData", JSON.stringify(playlistData))
      setFlashMessage({ type: "success", text: "Playlist fetched! Generating course structure..." })
      const videoTitles = playlistData.videos
        .filter((video) => video.title !== "Private video" && video.url && video.video_id)
        .map((video) => video.title)
      const structureResponse = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
        request: "create-course-from-playlist",
        body: {
          videoTitles: videoTitles,
        },
      })
      if (structureResponse.statusCode !== 200) {
        throw new Error("Failed to generate course structure")
      }
      const structureData = JSON.parse(structureResponse.body)
      localStorage.setItem("courseStructureData", JSON.stringify(structureData))
      setYoutubeProcessingData({
        courseData: {
          id: courseData._id,
          title: courseData.title,
          description: courseData.description,
        },
        playlistData: playlistData.videos,
        topicsData: structureData.courseStructure.topics,
      })
      setIsYouTubeModalOpen(false)
      setIsStructureModalOpen(true)
      setFlashMessage({ type: "success", text: "Course structure generated! Review and confirm." })
    } catch (error) {
      console.error("Error processing YouTube course:", error)
      setFlashMessage({ type: "error", text: `Error: ${error.message}` })
    } finally {
      setIsProcessingYouTube(false)
    }
  }

  const handleStructureConfirm = async (structuredCourse) => {
    setIsCreatingCourse(true)
    try {
      const courseId = youtubeProcessingData.courseData.id
      const playlistData = JSON.parse(localStorage.getItem("youtubePlaylistData") || "{}")
      for (const topic of structuredCourse) {
        const topicResponse = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
          request: "add-topic",
          body: {
            courseId: courseId,
            topicTitle: topic.title,
          },
        })
        if (topicResponse.statusCode !== 200) {
          throw new Error(`Failed to create topic: ${topic.title}`)
        }
        const topicData = JSON.parse(topicResponse.body)
        const topicId = topicData.topic.id
        for (const subtopic of topic.subtopics) {
          if (subtopic.video) {
            const subtopicResponse = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
              request: "add-subtopic",
              body: {
                courseId: courseId,
                topicId: topicId,
                subtopicTitle: subtopic.title,
                subtopicDescription: subtopic.video.description || "",
                videoUrl: subtopic.video.url,
                contentType: "video",
                isYoutubeVideo: true,
              },
            })
            if (subtopicResponse.statusCode !== 200) {
              console.error(`Failed to create subtopic: ${subtopic.title}`)
            }
          } else {
            const subtopicResponse = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
              request: "add-subtopic",
              body: {
                courseId: courseId,
                topicId: topicId,
                subtopicTitle: subtopic.title,
                subtopicDescription: "Content to be added later",
                contentType: "text",
              },
            })
            if (subtopicResponse.statusCode !== 200) {
              console.error(`Failed to create subtopic: ${subtopic.title}`)
            }
          }
        }
      }
      localStorage.setItem(
        "completedYoutubeCourse",
        JSON.stringify({
          courseId: courseId,
          structure: structuredCourse,
          createdAt: new Date().toISOString(),
        }),
      )
      localStorage.removeItem("youtubePlaylistData")
      localStorage.removeItem("courseStructureData")
      // Refresh courses after creating YouTube course
      const coursesResponse = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
        request: "get-all-courses", // Changed to get-all-courses to fetch all courses
        body: {
          classRoomId: localStorage.getItem("classRoomId"),
        },
      })
      if (coursesResponse.statusCode === 200) {
        const responseData = JSON.parse(coursesResponse.body)
        if (Array.isArray(responseData.courses)) {
          const formattedCourses = await Promise.all(
            responseData.courses.map(async (course) => {
              let thumbnailUrl = null
              if (course.thumbnailS3Uri) {
                try {
                  thumbnailUrl = await generatePresignedUrl(course.thumbnailS3Uri)
                } catch (error) {
                  console.error("Error generating presigned URL:", error)
                }
              }
              return {
                id: course.id || course._id,
                name: course.title,
                description: course.description,
                thumbnail: thumbnailUrl,
                rating: course.averageRating || 0,
                enrolledStudents: course.numberOfStudentsShared || 0,
                completionRate: course.averageCompletionPercentage || 0,
                shareWithStudents: course.studentsSharedWith && course.studentsSharedWith.length > 0,
              }
            }),
          )
          setCourses(formattedCourses)
        }
      }
      setIsStructureModalOpen(false)
      setYoutubeProcessingData({ courseData: null, playlistData: null, topicsData: null })
      setFlashMessage({ type: "success", text: "Course created successfully from YouTube playlist!" })
      setTimeout(() => {
        navigate(`/eLearning/course/${courseId}`)
      }, 2000)
    } catch (error) {
      console.error("Error creating course structure:", error)
      setFlashMessage({ type: "error", text: `Error creating course: ${error.message}` })
    } finally {
      setIsCreatingCourse(false)
    }
  }

  const handleDelete = (courseId) => {
    setDeleteConfirmation(courseId)
  }

  const confirmDelete = async () => {
    setIsDeletingCourse(true)
    try {
      const payload = {
        request: "delete-course",
        body: {
          courseId: deleteConfirmation,
        },
      }
      const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", payload)
      if (response.statusCode === 200) {
        setCourses(courses.filter((course) => course.id !== deleteConfirmation))
        setDeleteConfirmation(null)
        setFlashMessage({ type: "success", text: "Course deleted successfully" })
        setTimeout(() => {
          // No need to reload the entire window, state update is enough
          // window.location.reload();
        }, 1500)
      } else {
        throw new Error(JSON.parse(response.body).message || "Failed to delete course")
      }
    } catch (error) {
      console.error("Error deleting course:", error)
      setFlashMessage({ type: "error", text: "Error deleting course. Please try again." })
    } finally {
      setIsDeletingCourse(false)
    }
  }

  const getFilterLabel = () => {
    switch (activeFilter) {
      case "shared":
        return "Shared"
      case "not-shared":
        return "Not Shared"
      case "high-rating":
        return "High Rating"
      default:
        return "All"
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md text-center max-w-md mx-auto">
          <Loader className="animate-spin text-blue-600 mx-auto mb-3 sm:mb-4" size={36} />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Loading courses</h2>
          <p className="text-sm sm:text-base text-gray-600">Please wait while we fetch your courses...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md text-center max-w-md mx-auto">
          <AlertCircle className="text-red-500 mx-auto mb-3 sm:mb-4" size={36} />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Something went wrong</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 -mt-6">
      {flashMessage && <FlashMessage message={flashMessage} onClose={() => setFlashMessage(null)} />}
      <div className="container mx-auto px-2 md:px-4 py-0 max-w-7xl">
        {/* Header Section with Stats, Search, Filter, and Create Buttons */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden mt-6 md:mt-8">
          <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-3 md:px-4 py-3 md:py-3.5">
            {/* Mobile Layout */}
            <div className="block lg:hidden">
              <div className="flex items-center gap-2">
              
                {/* View Toggle */}
                <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                  <button
                    onClick={() => setViewMode("card")}
                    className={`p-1.5 rounded-l-lg transition-colors ${
                      viewMode === "card" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
                    }`}
                  >
                    <Grid3X3 size={12} />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-1.5 rounded-r-lg transition-colors ${
                      viewMode === "list" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
                    }`}
                  >
                    <List size={12} />
                  </button>
                </div>
                {/* Search */}
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="w-full px-3 py-1.5 pl-7 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-xs"
                  />
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-white/70" size={12} />
                </div>
                {/* Filter */}
                <div className="relative">
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-white/20 backdrop-blur-sm text-white text-xs font-medium border border-white/30"
                  >
                    <Filter size={12} />
                    <ChevronDown
                      size={10}
                      className={`transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
                    />
                  </button>
                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg z-10 border border-gray-200 py-1 animate-scale-in">
                      {[
                        { key: "all", label: "All" },
                        { key: "shared", label: "Shared" },
                        { key: "not-shared", label: "Not Shared" },
                        { key: "high-rating", label: "High Rating" },
                      ].map(({ key, label }) => (
                        <button
                          key={key}
                          onClick={() => {
                            setActiveFilter(key)
                            setShowFilterDropdown(false)
                          }}
                          className={`w-full px-2 py-1.5 text-xs text-left transition-colors hover:bg-gray-50 ${
                            activeFilter === key ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Create Buttons */}
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-white/20 backdrop-blur-sm text-white px-2 py-1.5 rounded-lg hover:bg-white/30 transition-colors duration-300 flex items-center gap-1 border border-white/30 text-xs font-medium"
                >
                  <Plus size={12} />
                </button>
                <button
                  onClick={() => setIsYouTubeModalOpen(true)}
                  className="bg-red-600/90 backdrop-blur-sm text-white px-2 py-1.5 rounded-lg hover:bg-red-700 transition-colors duration-300 flex items-center gap-1 border border-red-500/30 text-xs font-medium"
                >
                  <Youtube size={12} />
                </button>
              </div>
            </div>
            {/* Desktop Layout */}
            <div className="hidden lg:flex lg:items-center justify-between gap-4">
       
              {/* View Toggle */}
              <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                <button
                  onClick={() => setViewMode("card")}
                  className={`flex items-center gap-1 p-2 rounded-l-lg transition-colors text-xs ${
                    viewMode === "card" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
                  }`}
                  title="Card View"
                >
                  <Grid3X3 size={14} />
                  <span>Card</span>
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`flex items-center gap-1 p-2 rounded-r-lg transition-colors text-xs ${
                    viewMode === "list" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
                  }`}
                  title="List View"
                >
                  <List size={14} />
                  <span>List</span>
                </button>
              </div>
              {/* Search, Filter, and Create - Right side */}
              <div className="flex items-center gap-4">
                {/* Search */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search courses"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="w-64 px-4 py-2 pl-10 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-sm"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70" size={14} />
                </div>
                {/* Filter */}
                <div className="relative">
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/20 backdrop-blur-sm text-white text-sm font-medium border border-white/30 hover:bg-white/30 transition-colors"
                  >
                    <Filter size={14} />
                    <span>{getFilterLabel()}</span>
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
                    />
                  </button>
                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-1 w-36 bg-white rounded-md shadow-lg z-10 border border-gray-200 py-1 animate-scale-in">
                      {[
                        { key: "all", label: "All Courses" },
                        { key: "shared", label: "Shared" },
                        { key: "not-shared", label: "Not Shared" },
                        { key: "high-rating", label: "High Rating" },
                      ].map(({ key, label }) => (
                        <button
                          key={key}
                          onClick={() => {
                            setActiveFilter(key)
                            setShowFilterDropdown(false)
                          }}
                          className={`w-full px-3 py-2 text-sm text-left transition-colors hover:bg-gray-50 ${
                            activeFilter === key ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Create Buttons */}
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-white/20 backdrop-blur-sm text-white px-5 py-2 rounded-lg hover:bg-white/30 transition-colors duration-300 flex items-center gap-2 border border-white/30 text-sm font-medium"
                >
                  <Plus size={16} />
                  <span>Add Course</span>
                </button>
                <button
                  onClick={() => setIsYouTubeModalOpen(true)}
                  className="bg-red-600/90 backdrop-blur-sm text-white px-5 py-2 rounded-lg hover:bg-red-700 transition-colors duration-300 flex items-center gap-2 border border-red-500/30 text-sm font-medium"
                >
                  <Youtube size={16} />
                  <span>YouTube</span>
                </button>
              </div>
            </div>
          </div>
          {/* Content Section */}
          <div className="p-3 md:p-6">
            {courses.length === 0 ? (
              <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 text-indigo-600 mb-4">
                  <Book className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No courses yet</h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  Get started by creating your first course or importing content from YouTube
                </p>
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300 shadow-md"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add New Course
                  </button>
                  <button
                    onClick={() => setIsYouTubeModalOpen(true)}
                    className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-300 shadow-md"
                  >
                    <Youtube className="w-4 h-4 mr-2" />
                    Import from YouTube
                  </button>
                </div>
              </div>
            ) : getFilteredCourses().length === 0 ? (
              <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 text-amber-600 mb-4">
                  <Search className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No matching courses found</h3>
                <p className="text-gray-500 max-w-md mx-auto mb-6">
                  Try adjusting your search terms or filters to find what you're looking for.
                </p>
                <button
                  onClick={() => {
                    setSearchQuery("")
                    setActiveFilter("all")
                  }}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300"
                >
                  Clear Filters
                </button>
              </div>
            ) : viewMode === "card" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {getFilteredCourses().map((course) => (
                  <div
                    key={course.id}
                    className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group"
                  >
                    <div className="relative h-48">
                      {course.thumbnail ? (
                        <img
                          src={course.thumbnail || "/placeholder.svg"}
                          alt={course.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-indigo-100 to-blue-200 flex items-center justify-center">
                          <Book className="w-12 h-12 text-indigo-400" />
                        </div>
                      )}
                      <div className="absolute top-3 right-3">
                        <div
                          className={`p-2 rounded-full shadow-md transition-colors duration-300 ${
                            course.shareWithStudents ? "bg-indigo-600 text-white" : "bg-gray-400 text-white"
                          }`}
                          title={course.shareWithStudents ? "Shared with students" : "Not shared with students"}
                        >
                          <Share2 className="w-4 h-4" />
                        </div>
                      </div>
                      <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3">
                        <button
                          onClick={() => navigate(`/eLearning/course/${course.id}`)}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300 shadow-md"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(course.id)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-300 shadow-md"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1">{course.name}</h3>
                      <p className="text-gray-600 mb-4 line-clamp-2 text-sm">{course.description}</p>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center">
                          <Star className="w-4 h-4 text-yellow-400 mr-1" />
                          <span>{course.rating.toFixed(1)}</span>
                        </div>
                        <div className="flex items-center">
                          <Users className="w-4 h-4 text-indigo-500 mr-1" />
                          <span>{course.enrolledStudents}</span>
                        </div>
                        <div className="flex items-center">
                          <ChartBar className="w-4 h-4 text-green-500 mr-1" />
                          <span>{course.completionRate}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // List View
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Course Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rating
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Students
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Completion
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getFilteredCourses().map((course) => (
                        <tr key={course.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg flex items-center justify-center mr-3">
                                <Book className="w-4 h-4 text-white" />
                              </div>
                              <div className="text-sm font-medium text-gray-900 max-w-xs truncate">{course.name}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{course.description}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center">
                              <Star className="w-4 h-4 text-yellow-400 mr-1" />
                              <span>{course.rating.toFixed(1)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center">
                              <Users className="w-4 h-4 text-indigo-500 mr-1" />
                              <span>{course.enrolledStudents}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center">
                              <ChartBar className="w-4 h-4 text-green-500 mr-1" />
                              <span>{course.completionRate}%</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => navigate(`/eLearning/course/${course.id}`)}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                              >
                                <Edit size={14} className="mr-1" />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(course.id)}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                              >
                                <Trash2 size={14} className="mr-1" />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Regular Course Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all duration-300 animate-scale-in">
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 py-6 px-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">Add New Course</h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-indigo-100 text-sm mt-1">Create engaging educational content</p>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Course Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={newCourse.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                  placeholder="Enter course title"
                />
              </div>
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <div className="relative">
                  <textarea
                    id="description"
                    name="description"
                    value={newCourse.description}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    placeholder="Describe your course content and objectives"
                  />
                  <button
                    type="button"
                    onClick={generateAIDescription}
                    disabled={isGeneratingDescription}
                    className="absolute right-2 bottom-2 px-3 py-1 bg-purple-600 text-white text-xs rounded-md hover:bg-purple-700 transition-colors flex items-center shadow-sm"
                  >
                    {isGeneratingDescription ? (
                      <>
                        <Loader className="w-3 h-3 animate-spin mr-1" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <span className="mr-1">AI Generate</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M12 2a10 10 0 1 0 10 10H12V2z"></path>
                          <path d="M12 2a10 10 0 0 1 10 10h-10V2z"></path>
                          <path d="M12 22v-10h10"></path>
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Course Thumbnail</label>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-indigo-500 hover:text-indigo-500 transition-colors duration-300 text-center"
                >
                  {newCourse.thumbnail ? (
                    <span className="text-indigo-600 font-medium">{newCourse.thumbnail.name}</span>
                  ) : (
                    <span>Click to upload thumbnail image</span>
                  )}
                </button>
                <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300 disabled:bg-indigo-400 shadow-md font-medium"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <Loader className="w-4 h-4 animate-spin mr-2" />
                      Creating...
                    </div>
                  ) : (
                    "Create Course"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* YouTube Course Modal */}
      <YouTubeCourseModal
        isOpen={isYouTubeModalOpen}
        onClose={() => setIsYouTubeModalOpen(false)}
        onSubmit={handleYouTubeSubmit}
        isProcessing={isProcessingYouTube}
      />
      {/* Course Structure Modal */}
      <CourseStructureModal
        isOpen={isStructureModalOpen}
        onClose={() => setIsStructureModalOpen(false)}
        courseData={youtubeProcessingData.courseData}
        playlistData={youtubeProcessingData.playlistData}
        topicsData={youtubeProcessingData.topicsData}
        onConfirm={handleStructureConfirm}
        isCreating={isCreatingCourse}
      />
      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all duration-300 animate-scale-in">
            <div className="bg-gradient-to-r from-red-600 to-pink-600 py-6 px-6">
              <h2 className="text-2xl font-bold text-white flex items-center">
                <AlertCircle className="w-6 h-6 mr-2" />
                Delete Course
              </h2>
              <p className="text-red-100 text-sm mt-1">This action cannot be undone</p>
            </div>
            <div className="p-6">
              <div className="bg-red-50 p-4 rounded-lg mb-6">
                <p className="text-red-800 font-medium">Are you sure you want to delete this course?</p>
                <p className="text-red-600 text-sm mt-1">
                  All course content and student progress will be permanently removed.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeleteConfirmation(null)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
                  disabled={isDeletingCourse}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-300 shadow-md font-medium flex items-center justify-center"
                  disabled={isDeletingCourse}
                >
                  {isDeletingCourse ? (
                    <>
                      <Loader className="animate-spin mr-2 h-4 w-4" />
                      Deleting...
                    </>
                  ) : (
                    "Delete Course"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ELearning
